import qs from 'qs';
import { v4 as uuidv4 } from 'uuid';
import { httpClient } from 'providers/clients/httpClient';
import {
  GetInsightsWidgetExportResponse,
  GetInsightsWidgetExportTaskIdResponse,
  TCreateWidgetExportParams,
  TGetWidgetDataByIdParams,
  TGetWidgetExportParams,
} from 'utility/types/dataProvider';
import type {
  TCreateDashboardPayload,
  TCreateDashboardResponse,
  TDeleteDashboardParams,
  TGetDashboardsQp,
  TGetDashboardsResponse,
  TGetWidgetListParams,
  TGetWidgetListResponse,
  TPostWidgetPayload,
  TPostWidgetResponse,
  TPutWidgetPayload,
  TDeleteWidgetAlertByIdParams,
  TDeleteWidgetAlertByIdResponse,
  TGetWidgetAlertByIdParams,
  TGetWidgetAlertByIdResponse,
  TGetWidgetAlertsResponse,
  TGetWidgetAlertsParams,
  TPatchWidgetAlertByIdResponse,
  TPostWidgetAlertPayload,
  TPostWidgetAlertResponse,
  TPutWidgetAlertByIdParam,
  TPutWidgetAlertByIdPayload,
  TPutWidgetAlertByIdResponse,
  TWidgetAlert,
  TDuplicateDashboardPayload,
  TDuplicateDashboardResponse,
} from 'utility/types/dashboard';
import { TDashboard } from 'screens/dashboard/custom-dashboards/utils/constants';

// -----------------------------------------------
// ----------- DASHBOARDS PROVIDER ---------------
// -----------------------------------------------

/**
 * Adds unique instanceId to each widget in the layout to prevent duplication issues.
 * This ensures each widget instance can be uniquely identified even if multiple
 * instances of the same widget type exist on the dashboard.
 */
const addInstanceIdsToLayout = (layout: any[][]): any[][] => {
  if (!layout || !Array.isArray(layout)) return [];

  return layout.map((column) =>
    column.map((widget) => {
      // Skip string placeholders
      if (typeof widget === 'string') return widget;

      // Add instanceId if it doesn't exist
      return {
        ...widget,
        instanceId: widget.instanceId || uuidv4(),
      };
    }),
  );
};

export const dashboardsProvider = {
  // GET Dashboards List.
  getDashboards: async (params: TGetDashboardsQp): Promise<TGetDashboardsResponse> => {
    let path = `/insights/dashboard/`;

    const hasQuery = !!Object.keys(params || {})?.length;
    if (hasQuery) {
      path += `?${qs.stringify(params || {}, { arrayFormat: 'brackets' })}`;
    }

    try {
      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      return {
        status: data?.status || status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[getDashboards] Error', error);
      throw error;
    }
  },

  getDashboardById: async ({ id }: { id: string }) => {
    const path = `/insights/dashboard/${id}/`;

    try {
      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      // Add unique instanceId to each widget in the layout
      const dashboardData = data?.data;
      if (dashboardData?.layout) {
        dashboardData.layout = addInstanceIdsToLayout(dashboardData.layout);
      }

      return {
        status: data?.status || status,
        data: dashboardData,
      };
    } catch (error) {
      console.error('[getDashboardById] Error', error);
    }
  },

  // DELETE
  deleteDashboardById: async (id: TDeleteDashboardParams['id']) => {
    try {
      const path = `/insights/dashboard/${id}/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'DELETE',
      });

      return {
        status: data?.status || status,
        data: data?.data,
      };
    } catch (error) {
      console.error(`[deleteDashboardById] Error (Dashboard ID): ${id} Error:`, error);
      throw error;
    }
  },

  // CREATE Dashboard.
  createDashboard: async (
    payload: Partial<TCreateDashboardPayload>,
  ): Promise<TCreateDashboardResponse> => {
    const path = `/insights/dashboard/`;

    try {
      const { data } = await httpClient({
        url: path,
        method: 'POST',
        data: payload,
      });

      return data;
    } catch (error) {
      console.error('[createDashboard] Error:', error);
      throw error;
    }
  },

  // UPDATE Dashboard
  updateDashboard: async ({
    id,
    data: dashboardData,
  }: {
    id: string;
    data: Partial<TDashboard>;
  }) => {
    // TODO: add zod validation.
    try {
      const path = `/insights/dashboard/${id}/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'PUT',
        data: dashboardData,
        headers: { 'Content-Type': 'application/json' },
      });

      return {
        status: data?.status || status,
        data: data?.data,
      };
    } catch (error) {
      console.error(`[updateDashboard] Error (Dashboard ID): ${id} Error:`, error);
      throw error;
    }
  },

  duplicateDashboard: async ({
    id, // dashboard id.
    payload,
  }: {
    id: string;
    payload: TDuplicateDashboardPayload;
  }) => {
    try {
      const path = `/insights/dashboard/${id}/duplicate/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        data: payload,
        headers: { 'Content-Type': 'application/json' },
      });

      return {
        status: data?.status || status,
        data: data?.data,
      } as TDuplicateDashboardResponse;
    } catch (error) {
      console.error(`[duplicateDashboard] Error (Dashboard ID): ${id} Error:`, error);
      throw error;
    }
  },

  // ================================================
  // --- WIDGETS -----
  // ================================================

  // GET Widgets List
  getWidgets: async (params?: TGetWidgetListParams): Promise<TGetWidgetListResponse> => {
    let path = `/insights/widget/`;

    const hasQuery = !!Object.keys(params || {})?.length;
    if (hasQuery) {
      path += `?${qs.stringify(params || {}, { arrayFormat: 'brackets' })}`;
    }

    try {
      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      // Add instanceId to each widget for unique identification
      let widgetData = data?.data;
      if (widgetData) {
        if (Array.isArray(widgetData)) {
          widgetData = widgetData.map((widget: any) => ({
            ...widget,
            instanceId: uuidv4(),
          }));
        } else if (widgetData.items && Array.isArray(widgetData.items)) {
          widgetData.items = widgetData.items.map((widget: any) => ({
            ...widget,
            instanceId: uuidv4(),
          }));
        }
      }

      return {
        status: data?.status || status,
        data: widgetData,
      };
    } catch (error) {
      console.error('[getWidgets] Error', error);
      throw error;
    }
  },

  getWidgetDataById: async ({ id, query }: TGetWidgetDataByIdParams) => {
    try {
      let path = `/insights/widget/${id}/data/`;

      const hasQuery = Object.keys(query || {}).length > 0;
      if (hasQuery) {
        path += `?${qs.stringify(query || {}, {
          // TODO: API should have a standard for array formats see: httpClient.tsx -> qsOptions.
          arrayFormat: 'comma',
          encode: false,
        })}`;
      }

      const { data } = await httpClient({
        url: path,
        method: 'GET',
      });

      return data?.data;
    } catch (error) {
      console.error(`[getWidgetDataById] Error (Widget ID): ${id} Error:`, error);
      throw error;
    }
  },

  // POST
  createWidget: async (payload: TPostWidgetPayload): Promise<TPostWidgetResponse> => {
    const path = `/insights/widget/`;

    try {
      const { data } = await httpClient({
        url: path,
        method: 'POST',
        data: payload,
      });

      return data?.data;
    } catch (error) {
      console.error('[createWidget] Error:', error);
      throw error;
    }
  },

  // PUT
  updateWidget: async ({ id, data }: TPutWidgetPayload) => {
    const path = `/insights/widget/${id}/`;

    try {
      const { data: responseData } = await httpClient({
        url: path,
        method: 'PUT',
        data,
        headers: { 'Content-Type': 'application/json' },
      });

      return responseData?.data;
    } catch (error) {
      console.error(`[updateWidget] Error (Widget ID): ${id} Error:`, error);
      throw error;
    }
  },

  // =================================================
  // --- CSV EXPORTS FOR DASHBOARD WIDGETS -----
  // =================================================

  // Export to csv -- returns a taskId.
  createWidgetExport: async ({
    id,
    payload,
  }: TCreateWidgetExportParams): Promise<GetInsightsWidgetExportResponse> => {
    try {
      const path = `/insights/widget/${id}/export/`;
      const { data } = await httpClient({
        url: path,
        method: 'POST',
        data: payload,
      });

      return data?.data;
    } catch (error) {
      console.error(`[createWidgetExport] Error (Widget ID): ${id} Error:`, error);
      throw error;
    }
  },

  // Check export status.
  getWidgetExportStatus: async (
    taskId: string,
  ): Promise<{ status: number; isReady: boolean }> => {
    try {
      const path = `/insights/widget/export/${taskId}/`;
      const { status } = await httpClient({
        url: path,
        method: 'GET',
      });

      return {
        status,
        isReady: status === 200,
      };
    } catch (error: any) {
      console.error(`[getWidgetExportStatus] Error (Task ID): ${taskId} Error:`, error);

      // If it's a 404, the export might not exist yet or has expired
      if (error?.response?.status === 404) {
        return {
          status: 404,
          isReady: false,
        };
      }

      // If it's a 202, the export is still processing
      if (error?.response?.status === 202) {
        return {
          status: 202,
          isReady: false,
        };
      }

      throw error;
    }
  },

  // Get the exported data.
  getWidgetExport: async ({
    taskId,
    outputFileName,
  }: TGetWidgetExportParams): Promise<GetInsightsWidgetExportTaskIdResponse> => {
    try {
      let path = `/insights/widget/export/${taskId}/`;

      if (outputFileName) {
        path += `?${qs.stringify({ outputFileName })}`;
      }

      const { data } = await httpClient({
        url: path,
        method: 'GET',
      });

      return data;
    } catch (error) {
      console.error(
        `[getWidgetExport] Error (Task ID): ${taskId} Filename: ${outputFileName} Error:`,
        error,
      );
      throw error;
    }
  },

  // =================================================
  // --- ALERTS -----
  // =================================================
  getWidgetAlerts: async (
    widgetId: string[],
    params?: TGetWidgetAlertsParams,
  ): Promise<TGetWidgetAlertsResponse> => {
    let path = `/insights/alert/`;

    path += `?${qs.stringify({ ...(params || {}), widget: widgetId, limit: 25 }, { arrayFormat: 'brackets' })}`;

    try {
      const { data } = await httpClient({
        url: path,
        method: 'GET',
      });

      return data?.data;
    } catch (error) {
      console.error(`[getWidgetAlerts] Error (Widget ID): ${widgetId} Error:`, error);
      throw error;
    }
  },

  getWidgetAlertById: async ({
    id,
  }: TGetWidgetAlertByIdParams): Promise<TGetWidgetAlertByIdResponse | undefined> => {
    const path = `/insights/alert/${id}/`;

    try {
      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      return {
        status: data?.status || status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[getWidgetAlertById] Error', error);
    }
  },

  getWidgetAlertFields: async (widgetId: string) => {
    const path = `/insights/alert/fields/${widgetId}/`;

    try {
      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      return {
        status: data?.status || status,
        data: data?.data || [],
      };
    } catch (error: any) {
      // A 400 response means the widget has no alert_config — treat it as
      // "unsupported" rather than an unexpected error.
      if (error?.response?.status === 400 || error?.status === 400) {
        return { status: 400, data: [] };
      }
      console.error('[getWidgetAlertFields] Error', error);
      throw error;
    }
  },

  postWidgetAlert: async (
    payload: TPostWidgetAlertPayload,
  ): Promise<TPostWidgetAlertResponse> => {
    const path = `/insights/alert/`;

    try {
      const { data } = await httpClient({
        url: path,
        method: 'POST',
        data: payload,
      });

      return {
        status: data?.status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[postDashboardAlert] Error', error);
      throw error;
    }
  },

  putWidgetAlertById: async ({
    id,
    payload,
    headers,
  }: {
    id: TPutWidgetAlertByIdParam['id'];
    payload: Partial<TWidgetAlert>;
    headers?: Record<string, string>;
  }): Promise<TPutWidgetAlertByIdResponse> => {
    const path = `/insights/alert/${id}/`;

    try {
      const { data } = await httpClient({
        url: path,
        method: 'PUT',
        data: payload,
        headers,
      });
      return {
        status: data?.status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[putDashboardAlertById] Error', error);
      throw error;
    }
  },

  patchWidgetAlertById: async ({
    id,
    payload,
  }: {
    id: string;
    payload: TPutWidgetAlertByIdPayload;
  }): Promise<TPatchWidgetAlertByIdResponse> => {
    const path = `/insights/alert/${id}/`;

    try {
      const { data } = await httpClient({
        url: path,
        method: 'PATCH',
        data: payload,
      });
      return {
        status: data?.status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[patchWidgetAlertById] Error', error);
      throw error;
    }
  },

  deleteWidgetAlertById: async ({
    id,
  }: TDeleteWidgetAlertByIdParams): Promise<TDeleteWidgetAlertByIdResponse> => {
    const path = `/insights/alert/${id}/`;

    try {
      const { data, status } = await httpClient({
        url: path,
        method: 'DELETE',
      });

      return {
        status: data?.status || status,
        success: data?.success,
        message: data?.message,
      };
    } catch (error) {
      console.error('[deleteWidgetAlertById] Error', error);
      throw error;
    }
  },

  // https://townnews.atlassian.net/wiki/spaces/COSMOS/pages/3620175929/Webservice+API+Endpoints#GET%3A%2Finsights%2Falert%2Fparse_alerts%2F
  getWidgetAlertParsed: async ({ id }: { id: string }) => {
    const path = `/insights/alert/parse_alerts/?alert_id=${id}`;

    try {
      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      return {
        status: data?.status || status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[getWidgetAlertParsed] Error', error);
    }
  },

  // https://townnews.atlassian.net/wiki/spaces/COSMOS/pages/3620175929/Webservice+API+Endpoints#GET%3A%2Finsights%2Falert%2Ftrigger_trending_asset_lookup_cleanup%2F
  triggerWidgetAlertTrendingAssetLookupCleanup: async () => {
    const path = `/insights/alert/trigger_trending_asset_lookup_cleanup/`;

    try {
      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      return {
        status: data?.status || status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[triggerDashboardAlertTrendingAssetLookupCleanup] Error', error);
    }
  },
};

export type DashboardsProviderType = typeof dashboardsProvider;
